import { useState, useRef, useEffect } from 'react'
import { Outlet, NavLink, Link, useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useQuery } from '@tanstack/react-query'
import { useAuthStore } from '../store/authStore'
import { supportedLanguages } from '../i18n'
import { api, meApi } from '../api/client'
import { useInstances } from '../hooks/useInstances'
import { getAvatarColor, getInitials } from '../pages/ProfilePage'
import PageErrorBoundary from './PageErrorBoundary'
import { isSelfHosted, mailEnabled, branding } from '../config/edition'
import { identifyUser, resetAnalytics } from '../lib/analytics'

export default function Layout() {
  const logout = useAuthStore((s) => s.logout)
  const role = useAuthStore((s) => s.role)
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated)
  const appName = branding.appName
  const logoUrl: string | undefined = branding.logoUrl || undefined
  const navigate = useNavigate()
  const { t, i18n } = useTranslation()
  const [langOpen, setLangOpen] = useState(false)
  const [sidebarOpen, setSidebarOpen] = useState(false)
  const langRef = useRef<HTMLDivElement>(null)

  const { data: me } = useQuery({
    queryKey: ['me'],
    queryFn: meApi.get,
    staleTime: 5 * 60 * 1000,
    enabled: isAuthenticated,
  })
  // Keep authStore.plan honest with the DB-backed `me`. The JWT plan claim
  // is stamped at sign-in time and goes stale when an admin edits a user's
  // plan or after a checkout — without this sync, gated UI would still
  // believe the user is on the old plan until their access token expires.
  useEffect(() => {
    if (!me?.plan) return
    const current = useAuthStore.getState()
    if (current.plan !== me.plan && current.accessToken) {
      current.setAccessToken(current.accessToken, current.role ?? '', me.plan)
    }
  }, [me?.plan])
  // Identify the portal user to PostHog once per session. This is the line that
  // separates "someone using the product" from an anonymous website visitor in
  // every report. No-op until analytics consent is granted.
  useEffect(() => {
    if (!me?.id) return
    identifyUser({ id: me.id, email: me.email, role: me.role, plan: me.plan })
  }, [me?.id])
  const { data: instances } = useInstances()
  const [limitModalOpen, setLimitModalOpen] = useState(false)
  const atLimit = role !== 'admin' && me !== undefined && instances !== undefined && instances.length >= me.instance_limit

  const email = me?.email ?? ''
  const avatarColor = email ? getAvatarColor(email) : 'bg-gray-400'
  const initials = email ? getInitials(email) : '?'

  const handleLogout = () => {
    resetAnalytics() // drop the PostHog identity so the next person starts anonymous
    logout()
    navigate('/login')
  }

  const changeLanguage = (code: string) => {
    i18n.changeLanguage(code)
    setLangOpen(false)
    if (isAuthenticated) {
      api.patch('/auth/me', { language: code }).catch(() => {})
    }
  }

  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      if (langRef.current && !langRef.current.contains(e.target as Node)) {
        setLangOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClick)
    return () => document.removeEventListener('mousedown', handleClick)
  }, [])

  const currentLang = supportedLanguages.find((l) => l.code === i18n.language) ?? supportedLanguages[0]

  const navLinkClass = ({ isActive }: { isActive: boolean }) =>
    `flex items-center gap-2.5 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
      isActive ? 'bg-brand-50 text-brand-700' : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900'
    }`

  return (
    <div className="flex h-[100dvh] bg-gray-50">
      {/* Mobile backdrop */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-black/40 z-20 md:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      <aside
        className={`
          fixed inset-y-0 left-0 z-30 w-60 bg-white border-r border-gray-200 flex flex-col
          transform transition-transform duration-200 ease-in-out
          md:relative md:translate-x-0
          ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}
        `}
      >
        {/* Logo */}
        <div className="h-16 flex items-center px-5 border-b border-gray-100 shrink-0 pt-[env(safe-area-inset-top)] box-content">
          <div className="flex-1 min-w-0">
            {logoUrl ? (
              <img src={logoUrl} alt={appName} className="h-8 object-contain" />
            ) : (
              <span className="text-lg font-bold text-brand-600 truncate block">{appName}</span>
            )}
          </div>
          <button
            onClick={() => setSidebarOpen(false)}
            className="md:hidden ml-2 p-1.5 text-gray-400 hover:text-gray-600 rounded-md transition-colors"
            aria-label={t('common.closeMenu')}
          >
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Primary nav */}
        <nav className="flex-1 px-3 py-4 space-y-0.5 overflow-y-auto">
          <NavLink to="/dashboard" onClick={() => setSidebarOpen(false)} className={navLinkClass}>
            <svg className="w-4 h-4 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 10h16M4 14h16M4 18h16" />
            </svg>
            {t('layout.instances')}
          </NavLink>

          <NavLink
            to="/instances/new"
            onClick={(e) => {
              if (atLimit) {
                e.preventDefault()
                setLimitModalOpen(true)
                setSidebarOpen(false)
                return
              }
              setSidebarOpen(false)
            }}
            className={navLinkClass}
          >
            <svg className="w-4 h-4 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
            {t('layout.newInstance')}
          </NavLink>

          {/* Apps marketplace hidden for now (re-enable when entitlements + paid apps land). */}

          {role === 'admin' && (
            <>
              <NavLink to="/users" onClick={() => setSidebarOpen(false)} className={navLinkClass}>
                <svg className="w-4 h-4 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
                </svg>
                {t('layout.users')}
              </NavLink>

              <NavLink to="/templates" onClick={() => setSidebarOpen(false)} className={navLinkClass}>
                <svg className="w-4 h-4 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 5a1 1 0 011-1h14a1 1 0 011 1v2a1 1 0 01-1 1H5a1 1 0 01-1-1V5zM4 13a1 1 0 011-1h6a1 1 0 011 1v6a1 1 0 01-1 1H5a1 1 0 01-1-1v-6zM16 13a1 1 0 011-1h2a1 1 0 011 1v6a1 1 0 01-1 1h-2a1 1 0 01-1-1v-6z" />
                </svg>
                {t('layout.templates')}
              </NavLink>

              <NavLink to="/modules" onClick={() => setSidebarOpen(false)} className={navLinkClass}>
                <svg className="w-4 h-4 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
                </svg>
                {t('layout.modules')}
              </NavLink>

              <NavLink to="/admin/images" onClick={() => setSidebarOpen(false)} className={navLinkClass}>
                <svg className="w-4 h-4 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 16V8a2 2 0 00-1-1.73l-7-4a2 2 0 00-2 0l-7 4A2 2 0 003 8v8a2 2 0 001 1.73l7 4a2 2 0 002 0l7-4A2 2 0 0021 16z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3.27 6.96L12 12.01l8.73-5.05M12 22.08V12" />
                </svg>
                {t('layout.images')}
              </NavLink>

              {mailEnabled && (
                <NavLink to="/admin/mail" onClick={() => setSidebarOpen(false)} className={navLinkClass}>
                  <svg className="w-4 h-4 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                  </svg>
                  {t('layout.adminMail', { defaultValue: 'Mail control' })}
                </NavLink>
              )}

              <NavLink to="/admin/approvals" onClick={() => setSidebarOpen(false)} className={navLinkClass}>
                <svg className="w-4 h-4 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                {t('layout.adminApprovals', { defaultValue: 'Approvals' })}
              </NavLink>

              <NavLink to="/platform-settings" onClick={() => setSidebarOpen(false)} className={navLinkClass}>
                <svg className="w-4 h-4 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
                {t('layout.platformSettings')}
              </NavLink>
            </>
          )}
          {role === 'admin' && !isSelfHosted && (
            <NavLink
              to="/analytics"
              onClick={() => setSidebarOpen(false)}
              className={({ isActive }) =>
                `flex items-center gap-2 px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                  isActive ? 'bg-brand-50 text-brand-700' : 'text-gray-600 hover:bg-gray-100'
                }`
              }
            >
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
              </svg>
              {t('layout.analytics')}
            </NavLink>
          )}
          {role === 'admin' && (
            <NavLink
              to="/infrastructure"
              onClick={() => setSidebarOpen(false)}
              className={({ isActive }) =>
                `flex items-center gap-2 px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                  isActive ? 'bg-brand-50 text-brand-700' : 'text-gray-600 hover:bg-gray-100'
                }`
              }
            >
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 12H3l2-9h14l2 9h-2M5 12v7a1 1 0 001 1h12a1 1 0 001-1v-7M5 12h14M9 16h6" />
              </svg>
              {t('layout.infrastructure')}
            </NavLink>
          )}
          {role === 'admin' && (
            <NavLink
              to="/admin/traffic-logs"
              onClick={() => setSidebarOpen(false)}
              className={({ isActive }) =>
                `flex items-center gap-2 px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                  isActive ? 'bg-brand-50 text-brand-700' : 'text-gray-600 hover:bg-gray-100'
                }`
              }
            >
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
              {t('layout.trafficLogs')}
            </NavLink>
          )}
          {role === 'admin' && !isSelfHosted && (
            <NavLink
              to="/business"
              onClick={() => setSidebarOpen(false)}
              className={({ isActive }) =>
                `flex items-center gap-2 px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                  isActive ? 'bg-brand-50 text-brand-700' : 'text-gray-600 hover:bg-gray-100'
                }`
              }
            >
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              {t('layout.business')}
            </NavLink>
          )}
        </nav>

        {/* Bottom section */}
        <div className="px-3 pb-3 pt-2 border-t border-gray-100 space-y-0.5 shrink-0">
          {/* Language selector */}
          <div ref={langRef} className="relative">
            <button
              onClick={() => setLangOpen(!langOpen)}
              className="w-full text-left flex items-center gap-2.5 px-3 py-2 text-sm text-gray-600 hover:bg-gray-100 hover:text-gray-900 rounded-lg transition-colors"
            >
              <svg className="w-4 h-4 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5h12M9 3v2m1.048 9.5A18.022 18.022 0 016.412 9m6.088 9h7M11 21l5-10 5 10M12.751 5C11.783 10.77 8.07 15.61 3 18.129" />
              </svg>
              <span className="flex-1">{currentLang.label}</span>
              <svg
                className={`w-3 h-3 transition-transform ${langOpen ? 'rotate-180' : ''}`}
                fill="none" viewBox="0 0 24 24" stroke="currentColor"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </button>
            {langOpen && (
              <div className="absolute bottom-full mb-1 left-0 w-full bg-white border border-gray-200 rounded-xl shadow-lg py-1 z-50">
                {supportedLanguages.map((lang) => (
                  <button
                    key={lang.code}
                    onClick={() => changeLanguage(lang.code)}
                    className={`w-full text-left px-3 py-1.5 text-sm transition-colors ${
                      i18n.language === lang.code
                        ? 'bg-brand-50 text-brand-700 font-medium'
                        : 'text-gray-600 hover:bg-gray-100'
                    }`}
                  >
                    {lang.label}
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Divider */}
          <div className="h-px bg-gray-100 mx-1 my-1" />

          {/* User card → profile */}
          <Link
            to="/profile"
            onClick={() => setSidebarOpen(false)}
            className="flex items-center gap-3 px-3 py-2.5 rounded-lg hover:bg-gray-100 transition-colors group"
          >
            <div
              className={`w-8 h-8 rounded-full ${avatarColor} flex items-center justify-center text-white text-xs font-bold select-none shrink-0`}
            >
              {initials}
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-gray-800 truncate leading-tight">
                {email || '…'}
              </p>
              <p className="text-xs text-gray-400 leading-tight capitalize">
                {role === 'admin' ? t('common.admin') : t('layout.accountAndBilling')}
              </p>
            </div>
            <svg
              className="w-4 h-4 text-gray-300 group-hover:text-gray-500 shrink-0 transition-colors"
              fill="none" viewBox="0 0 24 24" stroke="currentColor"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
          </Link>

          {/* Sign out */}
          <button
            onClick={handleLogout}
            className="w-full text-left flex items-center gap-2.5 px-3 py-2 text-sm text-gray-500 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
          >
            <svg className="w-4 h-4 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
            </svg>
            {t('layout.signOut')}
          </button>

          {role === 'admin' && import.meta.env.VITE_COMMIT_HASH && (
            <div className="pt-1 flex items-center justify-center">
              <span className="text-xs text-gray-300 font-mono" title={t('layout.buildHash')}>
                {import.meta.env.VITE_COMMIT_HASH}
              </span>
            </div>
          )}
        </div>
      </aside>

      <div className="flex-1 flex flex-col min-w-0 overflow-hidden">
        {/* Mobile top bar */}
        <div className="h-14 flex items-center px-4 border-b border-gray-200 bg-white md:hidden shrink-0 pt-[env(safe-area-inset-top)] box-content">
          <button
            onClick={() => setSidebarOpen(true)}
            className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
            aria-label={t('common.openMenu')}
          >
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
            </svg>
          </button>
          <div className="ml-3 flex-1 min-w-0">
            {logoUrl ? (
              <img src={logoUrl} alt={appName} className="h-7 object-contain" />
            ) : (
              <span className="text-sm font-bold text-brand-600">{appName}</span>
            )}
          </div>
          {/* Mobile avatar link */}
          <Link to="/profile" className="ml-2 shrink-0">
            <div
              className={`w-8 h-8 rounded-full ${avatarColor} flex items-center justify-center text-white text-xs font-bold select-none`}
            >
              {initials}
            </div>
          </Link>
        </div>

        <main className="flex-1 overflow-auto pb-[env(safe-area-inset-bottom)]">
          <PageErrorBoundary>
            <Outlet />
          </PageErrorBoundary>
        </main>
      </div>

      {limitModalOpen && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4"
          onClick={() => setLimitModalOpen(false)}
        >
          <div
            className="bg-white rounded-2xl shadow-xl max-w-sm w-full p-6 text-center"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="w-16 h-16 rounded-full bg-brand-50 flex items-center justify-center mx-auto mb-4">
              <svg className="w-8 h-8 text-brand-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M9 17v-2a4 4 0 014-4h4m0 0l-3-3m3 3l-3 3M5 19V5a2 2 0 012-2h7l5 5v3" />
              </svg>
            </div>
            <h3 className="text-lg font-semibold text-gray-900">{t('layout.limitModal.title')}</h3>
            <p className="text-sm text-gray-500 mt-2 mb-6">
              {t('layout.limitModal.body', { used: instances?.length ?? 0, limit: me?.instance_limit ?? 0 })}
            </p>
            <div className="flex flex-col gap-2">
              {/* No self-serve billing in self-hosted — the admin raises limits. */}
              {!isSelfHosted && (
                <button
                  onClick={() => { setLimitModalOpen(false); navigate('/billing') }}
                  className="w-full py-2.5 px-4 bg-brand-600 text-white text-sm font-medium rounded-lg hover:bg-brand-700 transition-colors"
                >
                  {t('layout.limitModal.buy')}
                </button>
              )}
              <button
                onClick={() => setLimitModalOpen(false)}
                className="w-full py-2.5 px-4 text-gray-500 text-sm font-medium hover:text-gray-700 transition-colors"
              >
                {t('common.cancel')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
